import { useState, useEffect } from 'react'
import type { FaqSection, FaqItem } from '@limo/database'

import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  ChevronDown,
  ChevronRight,
  MoreHorizontal,
  Pencil,
  Plus,
  Trash2,
} from 'lucide-react'
import { cn } from '@/lib/utils'

interface FAQItemRowProps {
  item: FaqItem
  onEdit: () => void
  onDelete: () => void
}

function FAQItemRow({ item, onEdit, onDelete }: FAQItemRowProps) {
  const [isExpanded, setIsExpanded] = useState(false)

  return (
    <Collapsible open={isExpanded} onOpenChange={setIsExpanded}>
      <div className="rounded-lg border bg-muted/30 hover:bg-muted/50 transition-colors">
        <div className="flex items-center justify-between p-3">
          <CollapsibleTrigger asChild>
            <button
              className="flex items-center gap-2 flex-1 min-w-0 mr-4 text-left"
              type="button"
            >
              {isExpanded ? (
                <ChevronDown className="h-4 w-4 flex-shrink-0 text-muted-foreground" />
              ) : (
                <ChevronRight className="h-4 w-4 flex-shrink-0 text-muted-foreground" />
              )}
              <div className="flex-1 min-w-0">
                <p className={cn('font-medium', !isExpanded && 'truncate')}>
                  {item.question}
                </p>
                {!isExpanded && (
                  <p className="text-sm text-muted-foreground truncate">
                    {item.answer}
                  </p>
                )}
              </div>
            </button>
          </CollapsibleTrigger>
          <div className="flex items-center gap-2 flex-shrink-0">
            {!item.is_active && (
              <Badge variant="outline" className="text-xs">
                Inactive
              </Badge>
            )}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" className="h-8 w-8">
                  <MoreHorizontal className="h-4 w-4" />
                  <span className="sr-only">FAQ actions</span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={onEdit}>
                  <Pencil className="mr-2 h-4 w-4" />
                  Edit FAQ
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={onDelete}
                  className="text-destructive focus:text-destructive"
                >
                  <Trash2 className="mr-2 h-4 w-4" />
                  Delete FAQ
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
        <CollapsibleContent>
          <div className="px-3 pb-3 pt-0">
            <div className="pl-6 text-sm text-muted-foreground whitespace-pre-wrap">
              {item.answer}
            </div>
          </div>
        </CollapsibleContent>
      </div>
    </Collapsible>
  )
}

interface FAQSectionCardProps {
  section: FaqSection & { faq_items: FaqItem[] }
  onEditSection: () => void
  onDeleteSection: () => void
  onAddItem: () => void
  onEditItem: (itemId: string) => void
  onDeleteItem: (itemId: string) => void
  sectionExpanded?: boolean
}

export function FAQSectionCard({
  section,
  onEditSection,
  onDeleteSection,
  onAddItem,
  onEditItem,
  onDeleteItem,
  sectionExpanded,
}: FAQSectionCardProps) {
  const [isOpen, setIsOpen] = useState(true)

  useEffect(() => {
    if (sectionExpanded !== undefined) {
      setIsOpen(sectionExpanded)
    }
  }, [sectionExpanded])
  const itemCount = section.faq_items?.length ?? 0
  const activeItems = section.faq_items?.filter((item) => item.is_active) ?? []

  return (
    <Card>
      <Collapsible open={isOpen} onOpenChange={setIsOpen}>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CollapsibleTrigger asChild>
              <Button
                variant="ghost"
                className="flex items-center gap-2 p-0 h-auto hover:bg-transparent"
              >
                {isOpen ? (
                  <ChevronDown className="h-4 w-4" />
                ) : (
                  <ChevronRight className="h-4 w-4" />
                )}
                <CardTitle className="text-lg">{section.name}</CardTitle>
              </Button>
            </CollapsibleTrigger>
            <div className="flex items-center gap-2">
              <Badge variant={section.is_active ? 'default' : 'secondary'}>
                {itemCount} {itemCount === 1 ? 'item' : 'items'}
              </Badge>
              {!section.is_active && (
                <Badge variant="outline">Inactive</Badge>
              )}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="icon" className="h-8 w-8">
                    <MoreHorizontal className="h-4 w-4" />
                    <span className="sr-only">Section actions</span>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onClick={onEditSection}>
                    <Pencil className="mr-2 h-4 w-4" />
                    Edit Section
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={onDeleteSection}
                    className="text-destructive focus:text-destructive"
                  >
                    <Trash2 className="mr-2 h-4 w-4" />
                    Delete Section
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
          {section.description && (
            <CardDescription>{section.description}</CardDescription>
          )}
        </CardHeader>

        <CollapsibleContent>
          <CardContent className="pt-0">
            {activeItems.length === 0 && itemCount === 0 ? (
              <div className="text-center py-6 text-muted-foreground">
                <p className="mb-2">No FAQs in this section yet.</p>
                <Button variant="outline" size="sm" onClick={onAddItem}>
                  <Plus className="mr-2 h-4 w-4" />
                  Add FAQ
                </Button>
              </div>
            ) : (
              <div className="space-y-2">
                {section.faq_items
                  ?.sort((a, b) => a.display_order - b.display_order)
                  .map((item) => (
                    <FAQItemRow
                      key={item.id}
                      item={item}
                      onEdit={() => onEditItem(item.id)}
                      onDelete={() => onDeleteItem(item.id)}
                    />
                  ))}
                <Button
                  variant="ghost"
                  size="sm"
                  className="w-full mt-2"
                  onClick={onAddItem}
                >
                  <Plus className="mr-2 h-4 w-4" />
                  Add FAQ
                </Button>
              </div>
            )}
          </CardContent>
        </CollapsibleContent>
      </Collapsible>
    </Card>
  )
}
